import React from "react";
import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import type { ColumnDef } from "./IRCFundingPage";
import type { LedgerRow } from "./IRCFundingPage.adapters";

interface IRCFundingLedgerPageProps {
  rows: LedgerRow[];
  columns: ColumnDef[];
  totalBenefit: number;
  totalAnnualizedPremium: number;
  pageNumber: string | number;
}

const fmtCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const fmtCompact = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString("en-US")}`;
};

const IRCFundingLedgerPage = ({
  rows,
  columns,
  totalBenefit,
  totalAnnualizedPremium,
  pageNumber,
}: IRCFundingLedgerPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);

  const colorVariations = getColorVariations(secondaryColor, columns.length).reverse();

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          Funding Ledger
        </h1>

        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: colorVariations[i], fontSize: "8px" }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "8px" }}>
                {columns.map((col, j) => (
                  <td
                    key={j}
                    className="p-1 text-center pdf_table_border border-white"
                    style={col.cellStyle?.(row)}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
            <tr
              style={{
                backgroundColor: "#D1D5DB",
                color: "#000000",
                fontSize: "8px",
                fontWeight: 600,
              }}
            >
              {columns.map((col, i) => (
                <td key={i} className="p-1 text-center pdf_table_border border-white">
                  {col.total ? col.total(rows) : i === 0 ? "Total" : null}
                </td>
              ))}
            </tr>
          </tbody>
        </table>

        <div className="flex justify-between mt-4 px-1">
          <div>
            <span className="text-[9px] font-semibold uppercase text-gray-400 mr-2">
              Total Benefit
            </span>
            <span className="text-base font-bold text-gray-800">{fmtCompact(totalBenefit)}</span>
          </div>
          <div>
            <span className="text-[9px] font-semibold uppercase text-gray-400 mr-2">
              Total Annualized Premium
            </span>
            <span className="text-base font-bold text-gray-800">
              {fmtCurrency(totalAnnualizedPremium)}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCFundingLedgerPage;
